"use client";

import { useState } from "react";
import Image from "next/image";
import { Button, CircularProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownToLine } from "@awesome.me/kit-c10ef98ed3/icons/classic/regular";
import { useTranslations } from "next-intl";
import { Variant } from "@/hooks/useVariant";
import { useApi } from "@/contexts/ApiContext/ApiContext";
import { useSessionId } from "@/contexts/SessionContext/SessionContext";
import { trackCtaClick } from "@/utils/commonEvents";
import { getAssetUrl } from "@/utils/assetUrl";
import { useAssessment } from "@/contexts/AssessmentContext/AssessmentContext";

interface DownloadReportSectionProps {
  variant: Variant;
}

export default function DownloadReportSection({
  variant,
}: DownloadReportSectionProps) {
  const t = useTranslations("results-page.download-report-section");
  const api = useApi();
  const { sessionId } = useSessionId();
  const { inferenceId } = useAssessment();
  const [isLoading, setIsLoading] = useState(false);
  const canDownload = Boolean(sessionId && inferenceId);

  const handleDownload = async () => {
    if (isLoading || !sessionId || !inferenceId) return;

    trackCtaClick({
      componentName: "sleep_assessment_pro",
      clickedArea: "Download report",
      clickedText: t("button"),
    });

    setIsLoading(true);
    try {
      const now = new Date()
      const userDate = [
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
        now.getFullYear(),
      ].join("/");

      const { pdf_url } = await api.generatePdf(sessionId, inferenceId, userDate);
      // Navigate to PDF URL - CloudFront sets Content-Disposition: attachment which will trigger a download
      window.location.href = pdf_url;
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      // TODO: Show error toast/notification
    } finally {
      setIsLoading(false);
    }
  };

  const bgImage = getAssetUrl(
    variant === Variant.FEMALE
      ? "/images/results/download_sleep_report_female.webp"
      : "/images/results/download_sleep_report_default.webp",
  );

  return (
    <div
      className={`download-report-section-card download-report-section-card--${variant}`}
    >
      {/* Background image — stacked on mobile, absolute-positioned on desktop */}
      <div className="download-report-section-image-wrapper" aria-hidden="true">
        <Image
          className="download-report-section-image"
          src={bgImage}
          alt=""
          fill
          priority
          unoptimized
        />
        <div
          className={`download-report-section-gradient download-report-section-gradient--${variant}`}
        />
      </div>

      {/* Text content */}
      <div
        className={`download-report-section-content download-report-section-content--${variant}`}
      >
        <h2 className="headline download-report-section-headline">
          {t(`headline-${variant}`)}
        </h2>
        <p className="download-report-section-body">{t("body")}</p>
        <div className="download-report-section-secure">
          <Image
            src={getAssetUrl("/images/secure_icon.svg")}
            alt=""
            width={26}
            height={32}
            unoptimized
          />
          <span className="download-report-section-secure-label">
            {t("secure-label")}
          </span>
        </div>
        <Button
          className={`download-report-section-button download-report-section-button--${variant}`}
          variant="contained"
          color="secondary"
          disableElevation
          disableRipple
          disabled={!canDownload || isLoading}
          onClick={handleDownload}
          endIcon={
            isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <FontAwesomeIcon icon={faArrowDownToLine} />
            )
          }
        >
          {t("button")}
        </Button>
      </div>
    </div>
  );
}
